/**
 * Passage simulation and departure-time advice.
 *
 * Given a plotted route and a departure time, walk the route leg by leg, solving
 * the course-to-steer triangle against the tidal stream at each step (the tide is
 * re-read as the clock advances), to get how long the passage takes. Sweep the
 * departure across a window and the best slot for a fair tide falls out.
 *
 * Honest about its inputs: the tide comes from sparse chart diamonds (nearest
 * one to each point), so this is a planning estimate to compare departure slots,
 * not a guarantee. Wind/leeway is a fixed offset for now (0 by default).
 */
import { courseToSteer, rhumbBearingDeg, rhumbDistanceNm, rhumbDestination } from '@/nav';
import { streamAtPosition } from '@/tides';

/** Stream at a position and instant: set (deg true the tide flows TOWARDS) + rate (kn). */
export type StreamFn = (position: [number, number], ms: number) => { setDeg: number; rateKn: number };

export interface LegResult {
  distanceNm: number;
  durationMin: number;
  /** Average speed made good along the leg, kn. */
  avgSogKn: number;
  /** True if the tide was too strong to hold the track at some point on the leg. */
  unachievable: boolean;
}

export interface PassageResult {
  departMs: number;
  totalNm: number;
  /** Sailing time only (sum of the legs), minutes. */
  totalMin: number;
  /** Time stopped at via-stops along the way, minutes. */
  dwellMin: number;
  /** Door-to-door elapsed time: sailing + stops, minutes (what the ETA uses). */
  elapsedMin: number;
  legs: LegResult[];
  /** True if any leg was unachievable at the chosen departure. */
  unachievable: boolean;
}

export interface SimOptions {
  /** Override the tide source (tests inject a fixed field); defaults to the chart diamonds. */
  streamFn?: StreamFn;
  /** Constant leeway offset on the heading, degrees. Default 0. */
  leewayDeg?: number;
  /** Max time step, hours (the tide is re-read each step). Default 0.25 (15 min). */
  stepH?: number;
  /**
   * Hours stopped at each waypoint, indexed to match `waypoints`. A dwell is
   * applied on ARRIVAL at that waypoint, before the next leg starts, so the clock
   * (and the tide every downstream leg reads) advances by it. Index 0 (the start)
   * and the final index are normally 0. Omit for no stops.
   */
  dwellsH?: number[];
}

const MS_PER_H = 3_600_000;

/** Walk one route from `departMs` and return the timing. */
export function simulatePassage(
  waypoints: [number, number][],
  departMs: number,
  cruiseKn: number,
  opts: SimOptions = {},
): PassageResult {
  const streamFn = opts.streamFn ?? streamAtPosition;
  const leewayDeg = opts.leewayDeg ?? 0;
  const stepH = opts.stepH ?? 0.25;
  const dwellsH = opts.dwellsH ?? [];
  const speed = Math.max(0.1, cruiseKn);

  let t = departMs;
  let totalNm = 0;
  let dwellMin = 0;
  let anyUnachievable = false;
  const legs: LegResult[] = [];

  for (let i = 0; i + 1 < waypoints.length; i++) {
    const a = waypoints[i];
    const b = waypoints[i + 1];
    const bearing = rhumbBearingDeg(a, b);
    const legDist = rhumbDistanceNm(a, b);
    totalNm += legDist;

    let covered = 0;
    let legHours = 0;
    let legUnachievable = false;
    let guard = 0;
    while (covered < legDist - 1e-4 && guard++ < 5000) {
      const pos = rhumbDestination(a, bearing, covered);
      const stream = streamFn(pos, t);
      const cts = courseToSteer({
        from: pos,
        to: b,
        tidalSet: stream.setDeg,
        tidalRate: stream.rateKn,
        boatSpeed: speed,
        leewayDeg,
      });
      // Foul tide stronger than the boat: can't hold the track. Crawl and flag it,
      // so this departure scores badly rather than silently producing a NaN.
      let sog = cts.sogKnots;
      if (cts.unachievable || !Number.isFinite(sog) || sog <= 0) {
        sog = 0.05;
        legUnachievable = true;
        anyUnachievable = true;
      }
      const remaining = legDist - covered;
      const dtH = Math.min(stepH, remaining / sog);
      covered += sog * dtH;
      legHours += dtH;
      t += dtH * MS_PER_H;
    }

    legs.push({
      distanceNm: legDist,
      durationMin: legHours * 60,
      avgSogKn: legHours > 0 ? legDist / legHours : 0,
      unachievable: legUnachievable,
    });

    // Stopped at the waypoint we've just reached? Advance the clock by the dwell
    // before the next leg, so every downstream leg reads the tide at the later
    // time and the arrival ETA carries the stop.
    const dwellH = Math.max(0, dwellsH[i + 1] ?? 0);
    if (dwellH > 0) {
      t += dwellH * MS_PER_H;
      dwellMin += dwellH * 60;
    }
  }

  const totalMin = legs.reduce((s, l) => s + l.durationMin, 0);
  return {
    departMs,
    totalNm,
    totalMin,
    dwellMin,
    elapsedMin: totalMin + dwellMin,
    legs,
    unachievable: anyUnachievable,
  };
}

export interface DepartureOption {
  departMs: number;
  durationMin: number;
  unachievable: boolean;
}

export interface DepartureAdvice {
  options: DepartureOption[];
  /** Quickest achievable slot (falls back to overall quickest if all are unachievable). */
  best: DepartureOption;
  /** Leaving at the window start ("now"). */
  now: DepartureOption;
  /** Minutes saved by the best slot vs leaving now (>= 0). */
  savingMin: number;
}

/**
 * Sweep departure times across [windowStartMs, windowEndMs] and recommend the
 * quickest. The first option is "leave now" (the window start), so the saving is
 * relative to going straight away.
 */
export function recommendDeparture(
  waypoints: [number, number][],
  windowStartMs: number,
  windowEndMs: number,
  stepMin: number,
  cruiseKn: number,
  opts: SimOptions = {},
): DepartureAdvice | null {
  if (waypoints.length < 2 || windowEndMs <= windowStartMs || stepMin <= 0) return null;
  const stepMs = stepMin * 60_000;
  const options: DepartureOption[] = [];
  for (let t = windowStartMs; t <= windowEndMs; t += stepMs) {
    const r = simulatePassage(waypoints, t, cruiseKn, opts);
    // Elapsed (sailing + any stops), so the times the advisor shows are real ETAs.
    options.push({ departMs: t, durationMin: r.elapsedMin, unachievable: r.unachievable });
  }
  const now = options[0];
  const achievable = options.filter((o) => !o.unachievable);
  const pool = achievable.length ? achievable : options;
  const best = pool.reduce((a, b) => (b.durationMin < a.durationMin ? b : a), pool[0]);
  return { options, best, now, savingMin: Math.max(0, now.durationMin - best.durationMin) };
}

export interface ArrivalOption {
  departMs: number;
  /** Door-to-door elapsed (sailing + stops), minutes. */
  durationMin: number;
  arriveMs: number;
  unachievable: boolean;
}

export interface ArrivalAdvice {
  targetMs: number;
  options: ArrivalOption[];
  /** Best departure that still arrives by the target: the SHORTEST passage among
   *  them (the fairest tide), so you ride a fair tide rather than leave at the last
   *  possible moment. Null if none meet the target. */
  best: ArrivalOption | null;
  /** Latest departure that still arrives by the target (leave as late as you can). */
  latest: ArrivalOption | null;
  /** Earliest you could possibly arrive (across achievable departures), so an
   *  infeasible target can still say "the soonest you'd get there is...". */
  earliest: ArrivalOption | null;
  /** True when at least one achievable departure arrives at/before the target. */
  feasible: boolean;
  /** True when every swept departure was unachievable (foul tide throughout). */
  allFoul: boolean;
}

/**
 * Arrival-time targeting: the inverse of recommendDeparture. Sweep departures
 * across [windowStart, min(windowEnd, target)], simulate each (the passage time
 * varies with departure as the tidal vector turns), and work out which departures
 * arrive at or before the target. Recommends the fair-tide one (shortest passage)
 * and reports the latest possible, or, if none can make it, the earliest arrival.
 */
export function recommendForArrival(
  waypoints: [number, number][],
  windowStartMs: number,
  windowEndMs: number,
  stepMin: number,
  cruiseKn: number,
  targetMs: number,
  opts: SimOptions = {},
): ArrivalAdvice | null {
  if (waypoints.length < 2 || stepMin <= 0) return null;
  const end = Math.min(windowEndMs, targetMs);
  const stepMs = stepMin * 60_000;
  const options: ArrivalOption[] = [];
  for (let t = windowStartMs; t <= end; t += stepMs) {
    const r = simulatePassage(waypoints, t, cruiseKn, opts);
    options.push({
      departMs: t,
      durationMin: r.elapsedMin,
      arriveMs: t + r.elapsedMin * 60_000,
      unachievable: r.unachievable,
    });
  }
  const achievable = options.filter((o) => !o.unachievable);
  const meeting = achievable.filter((o) => o.arriveMs <= targetMs);
  const feasible = meeting.length > 0;
  const best = feasible ? meeting.reduce((a, b) => (b.durationMin < a.durationMin ? b : a), meeting[0]) : null;
  const latest = feasible ? meeting.reduce((a, b) => (b.departMs > a.departMs ? b : a), meeting[0]) : null;
  const pool = achievable.length ? achievable : options;
  const earliest = pool.length ? pool.reduce((a, b) => (b.arriveMs < a.arriveMs ? b : a), pool[0]) : null;
  return { targetMs, options, best, latest, earliest, feasible, allFoul: achievable.length === 0 };
}

export * from './polar';
export * from './autoRoute';
export * from './plan';
export * from './pilotageSections';
export * from './tidalGates';
export * from './comfort';
export * from './offlinePackage';
